import {
  ListingLoanInfo,
  ClientEventName,
  ClientEventSource,
  BucketedMonthlyHomeExpenses
} from '../../../../api/gqlaxy/gql/generated/graphql'
import Section from '../Section/Section'
import { Waypoint } from 'react-waypoint'
import { useIntl, defineMessages } from 'react-intl'
import { useCreateClientEvent } from '../../../../hooks'
import { useCallback, useEffect, useState } from 'react'
import { useTrackingContext } from '../../../../providers/tracking'
import { useTrackingDebounceSetters } from '../../../../hooks/tracking'
import { Flex, Heading, Text, Stack, Box } from '@homebotapp/hb-react-component-catalog'
import { EstimatedMonthlyPaymentBreakdown } from './EstimatedMonthlyPaymentBreakdown/EstimatedMonthlyPaymentBreakdown'
import { LISTINGS_DETAILED, ESTIMATED_PAYMENT_BREAKDOWN } from '../../../../constants/tracking/trackingEventConstants'
import {
  BUY_DOWN_TYPE,
  NO_CLEAR_BUY_DOWN_TYPE_FOUND
} from './EstimatedMonthlyPaymentBreakdown/EstimatedMonthlyPaymentBreakdownBuyDownToggle/BuyDownType'
import { getCalculatedBuyDownType } from './Utilities/EstimatedMonthlyPaymentUtilities'

export const MSG = defineMessages({
  head: {
    id: 'estimatedMonthlyPayment.head',
    defaultMessage: 'Your estimated payment'
  },
  subhead: {
    id: 'estimatedMonthlyPayment.subhead',
    defaultMessage: 'Play with the numbers to fit your needs'
  },
  pmt: {
    id: 'estimatedMonthlyPayment.pmt',
    defaultMessage: '{pmt} / month'
  },
  hoaSpelledOut: {
    id: 'estimatedMonthlyPayment.hoaSpelledOut',
    defaultMessage: "Homeowner's Association"
  },
  assumableToggle: {
    id: 'estimatedMonthlyPayment.assumableToggle',
    defaultMessage: 'Include assumable loan'
  }
})

export interface EstimatedMonthlyPaymentProps {
  address?: string
  assumableMortgageLoanInfo?: ListingLoanInfo | null
  assumableMortgageMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  assumableMortgageSecondLoanInfo?: ListingLoanInfo | null
  assumableToggle?: boolean
  bucketedMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  concessionAmount?: number
  homePrice: number
  isAssumable?: boolean
  isCalculatorsPage?: boolean
  listingBuyDownType?: BUY_DOWN_TYPE
  listingHasBuyDown?: boolean
  listingId?: string
  listingZipCode?: string
  loanInfo?: ListingLoanInfo | null
  setAssumableToggle?: (isAssumable: boolean) => void
}

export const EstimatedMonthlyPayment = ({
  address,
  assumableMortgageLoanInfo,
  assumableMortgageMonthlyHomeExpenses,
  assumableMortgageSecondLoanInfo,
  assumableToggle,
  bucketedMonthlyHomeExpenses,
  concessionAmount,
  homePrice,
  isAssumable,
  isCalculatorsPage = false,
  listingBuyDownType = NO_CLEAR_BUY_DOWN_TYPE_FOUND,
  listingHasBuyDown = false,
  listingId,
  listingZipCode,
  loanInfo,
  setAssumableToggle
}: EstimatedMonthlyPaymentProps) => {
  const intl = useIntl()
  const { trackingClient } = useTrackingContext()
  const [hasUsedMortgageCalculator, setHasUsedMortgageCalculator] = useState(false)

  const [downPaymentCash, setDownPaymentCash] = useState(
    localStorage?.getItem('downPaymentCash') ||
      (
        localStorage?.getItem('downPaymentPercentage') &&
        (Number(homePrice) * Number(localStorage?.getItem('downPaymentPercentage'))) / 100
      )?.toFixed(2) ||
      loanInfo?.downpayment.toFixed(2) ||
      '0'
  )

  const calculatedBuyDownType = getCalculatedBuyDownType(
    concessionAmount,
    Number(homePrice) - Number(downPaymentCash),
    listingBuyDownType
  )

  const [buyDownToggle, setBuyDownToggle] = useState(
    listingHasBuyDown || calculatedBuyDownType !== NO_CLEAR_BUY_DOWN_TYPE_FOUND
  )

  const { mutateAsync: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })
  const [trackModuleViewed] = useTrackingDebounceSetters(
    () =>
      trackingClient?.moduleViewed({
        event_source: LISTINGS_DETAILED,
        module_name: ESTIMATED_PAYMENT_BREAKDOWN
      })
  )

  const createUsedMortgageCalculatorEvent = useCallback(() => {
    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.UsedMortgageCalculator,
        eventSource: isCalculatorsPage ? ClientEventSource.Calculators : ClientEventSource.ListingDetailed,
        listingId: listingId || undefined
      }
    })
  }, [createClientEvent, listingId])

  const createUsedAssumableMortgageCalculatorEvent = useCallback(() => {
    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.UsedAssumableMortgageCalculator,
        eventSource: ClientEventSource.ListingDetailed,
        listingId: listingId || undefined
      }
    })
  }, [createClientEvent, listingId])

  useEffect(() => {
    if (hasUsedMortgageCalculator) {
      createUsedMortgageCalculatorEvent()
      trackingClient?.clientUsedMortgageCalculator({ pathname: window.location.pathname })
      if (assumableToggle) {
        createUsedAssumableMortgageCalculatorEvent()
      }
    }
  }, [hasUsedMortgageCalculator, assumableToggle])

  useEffect(() => {
    setHasUsedMortgageCalculator(false)
  }, [listingId, homePrice])

  return (
    <Section>
      <Section.Body>
        <Waypoint onEnter={trackModuleViewed} bottomOffset='25%' />
        <Stack direction='column' alignItems='center' justifyContent='space-between' gap={1}>
          <Flex w='100%'>
            <Stack direction='column' alignItems='center' width='100%'>
              <Flex mb={2} alignItems='center' direction='column'>
                <Heading as='h4' size='md' fontWeight='bold' textAlign='left'>
                  {intl.formatMessage(MSG.head)}
                </Heading>
                <Text textAlign='center' size='xs'>
                  {intl.formatMessage(MSG.subhead)}
                </Text>
              </Flex>
            </Stack>
          </Flex>
          <Box opacity={1} height='auto' width='100%'>
            <EstimatedMonthlyPaymentBreakdown
              address={address}
              assumableBucketedMonthlyHomeExpenses={assumableMortgageMonthlyHomeExpenses}
              assumableMortgageLoanInfo={assumableMortgageLoanInfo}
              assumableMortgageSecondLoanInfo={assumableMortgageSecondLoanInfo}
              assumableToggle={assumableToggle}
              setAssumableToggle={setAssumableToggle}
              bucketedMonthlyHomeExpenses={bucketedMonthlyHomeExpenses}
              buyDownToggle={buyDownToggle}
              concessionAmount={concessionAmount}
              homePrice={homePrice}
              isCalculatorsPage={isCalculatorsPage}
              listingBuyDownType={listingBuyDownType}
              listingHasBuyDown={listingHasBuyDown}
              listingId={listingId}
              listingZipCode={listingZipCode}
              loanInfo={loanInfo}
              isAssumable={isAssumable}
              setHasUsedMortgageCalculator={setHasUsedMortgageCalculator}
              setBuyDownToggle={setBuyDownToggle}
            />
          </Box>
        </Stack>
      </Section.Body>
    </Section>
  )
}
